import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import { useAuth } from '../../components/navigation/AuthenticationGuard';
import { postsService } from '../../services/postsService';
import MetricCard from './components/MetricCard';
import QuickActionCard from './components/QuickActionCard';
import RecentPostItem from './components/RecentPostItem';
import ActivityItem from './components/ActivityItem';
import SystemAlert from './components/SystemAlert';
import Button from '../../components/ui/Button';


const AdminDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const allPosts = await postsService?.getAllPosts();
      setPosts(allPosts);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const metrics = {
    totalPosts: posts?.length || 0,
    publishedPosts: posts?.filter(p => p?.status === 'published')?.length || 0,
    draftPosts: posts?.filter(p => p?.status === 'draft')?.length || 0,
    recentPosts: posts?.slice(0, 5) || []
  };


  const quickActions = [
  {
    id: 1,
    title: "Create New Post",
    description: "Start writing a new blog post with full editing capabilities",
    icon: "PenSquare",
    iconColor: "var(--color-primary)",
    actionLabel: "New Post",
    actionPath: "/admin/posts/new"
  },
  {
    id: 2,
    title: "Manage Posts",
    description: "View, edit, and organize all blog posts with status controls",
    icon: "FolderOpen",
    iconColor: "var(--color-secondary)",
    actionLabel: "View All Posts",
    actionPath: "/admin-posts-management"
  },
  {
    id: 3,
    title: "View Public Blog",
    description: "See how your published content appears to visitors",
    icon: "ExternalLink",
    iconColor: "var(--color-accent)",
    actionLabel: "Go to Blog",
    actionPath: "/blog-listing"
  }];


  const recentActivity = [
  {
    id: 1,
    type: "published",
    description: "Published \'Building Reliable Systems: A Technical Deep Dive'",
    user: "Sarah Chen",
    timestamp: "2026-01-24T20:30:00Z"
  },
  {
    id: 2,
    type: "updated",
    description: "Updated draft \'Operational Clarity Through Systems Coordination'",
    user: "Emily Watson",
    timestamp: "2026-01-24T19:15:00Z"
  },
  {
    id: 3,
    type: "created",
    description: "Created new post \'Infrastructure as Code Best Practices'",
    user: "Michael Rodriguez",
    timestamp: "2026-01-24T17:45:00Z"
  },
  {
    id: 4,
    type: "updated",
    description: "Updated hero image for \'Reducing Cognitive Load'",
    user: "Sarah Chen",
    timestamp: "2026-01-24T15:20:00Z"
  },
  {
    id: 5,
    type: "published",
    description: "Published \'Reducing Cognitive Load in Engineering Workflows'",
    user: "Michael Rodriguez",
    timestamp: "2026-01-24T12:00:00Z"
  }];


  const systemAlerts = [
  {
    id: 1,
    severity: "info",
    title: "System Update",
    message: "Platform maintenance scheduled for January 28, 2026 at 2:00 AM UTC. Expected downtime: 30 minutes."
  },
  {
    id: 2,
    severity: "success",
    title: "Backup Complete",
    message: "Daily content backup completed successfully at 12:00 AM UTC. All data secured."
  }];


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="content-area">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <div className="content-area">
        <div className="max-w-7xl mx-auto py-6 md:py-8">
          <BreadcrumbNavigation />
          
          <div className="mt-6 md:mt-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-2xl font-semibold text-foreground mb-2">
                  Dashboard
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Content management and platform administration
                </p>
              </div>
              <Button
                variant="default"
                size="lg"
                onClick={() => navigate('/admin/posts/new')}
                iconName="Plus"
                iconPosition="left">

                New Post
              </Button>
            </div>

            {systemAlerts?.length > 0 &&
            <div className="mb-6 md:mb-8 space-y-3">
                {systemAlerts?.map((alert) =>
              <SystemAlert key={alert?.id} alert={alert} />
              )}
              </div>
            }

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-10">
              <MetricCard
                title="Total Posts"
                value={metrics?.totalPosts}
                icon="FileText"
                iconColor="var(--color-primary)"
                change="+12%"
                changeType="increase"
                trendLabel="from last month"
              />
              <MetricCard
                title="Published"
                value={metrics?.publishedPosts}
                icon="CheckCircle"
                iconColor="var(--color-success)"
                change="+8%"
                changeType="increase"
                trendLabel="from last month"
              />
              <MetricCard
                title="Drafts"
                value={metrics?.draftPosts}
                icon="Edit3"
                iconColor="var(--color-warning)"
                change="-3%"
                changeType="decrease"
                trendLabel="from last month"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10">
              {quickActions?.map((action) =>
              <QuickActionCard key={action?.id} {...action} />
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground">Recent Posts</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/admin-posts-management')}
                    iconName="ArrowRight"
                    iconPosition="right">

                    View All
                  </Button>
                </div>
                <div className="space-y-4">
                  {metrics?.recentPosts?.length > 0 ? (
                    metrics?.recentPosts?.map((post) => (
                      <RecentPostItem
                        key={post?.id}
                        title={post?.title}
                        status={post?.status}
                        date={post?.updatedAt || post?.createdAt}
                        onClick={() => navigate('/admin-post-editor', { state: { post } })}
                      />
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No posts yet. Create your first post!
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4 md:mb-6">
                  Recent Activity
                </h2>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="space-y-2">
                    {recentActivity?.map((activity) =>
                    <ActivityItem key={activity?.id} activity={activity} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

};

export default AdminDashboard;